import React from 'react';
import { ClipLoader } from 'react-spinners';
import { getConfig } from '../config';
import NotificationToast from '../components/NotificationToast';
import PaymentPanel from '../components/PaymentPanel';
import useMercadoLibrePaymentFlow from '../hooks/useMercadoLibrePaymentFlow';

const MercadoLibreInterface = () => {
    const { apiBaseURL } = getConfig();
    const { state, actions, calculations } = useMercadoLibrePaymentFlow({ apiBaseURL });

    const {
        id,
        isIdLocked,
        customerData,
        payments,
        selectedPayments,
        error,
        checkoutError,
        notification,
        isLoading,
        paymentOption,
        partialPaymentAmount,
        appliedAmounts,
        isPreparingCheckout,
    } = state;

    const {
        setId,
        setPartialPaymentAmount,
        handleValidateCustomer,
        handleResetCustomer,
        handlePaymentOptionChange,
        handleCheckboxChange,
        handleGeneralPay,
    } = actions;

    const {
        calculateAbonoAplicado,
        calculatePendingBalanceOnlyForDisplay,
        calculateExcess,
        calculateTotalPagarComplete,
    } = calculations;

    return (
        <div className="min-h-screen flex items-center justify-center px-4 background-container">
            <div className="flex items-start justify-center px-4 bg-transparent w-full">
                <NotificationToast notification={notification} />

                <div
                    className={`w-full max-w-md md:max-w-md lg:max-w-lg rounded-[28px] shadow-xl overflow-hidden relative ${
                        customerData ? 'bg-white' : 'bg-white/70 backdrop-blur-md'
                    }`}
                >
                    <div className="p-8 space-y-6">
                        <div className="flex justify-center mb-2">
                            <img
                                src="/logo-dermodis.png?v=20260722-2"
                                alt="Logo Dermodis"
                                className="h-40 w-full max-w-[420px] object-contain"
                            />
                        </div>

                        <div>
                            <label htmlFor="id" className="block text-gray-800 font-semibold mb-2">
                                Documento de identidad (DNI/RUC):
                            </label>

                            <div className={`flex items-center gap-2 ${isIdLocked && !isLoading ? '' : 'w-full'}`}>
                                <input
                                    type="text"
                                    id="id"
                                    value={id}
                                    onChange={(event) => setId(event.target.value)}
                                    className={`${isIdLocked && !isLoading ? 'flex-1' : 'w-full'} px-4 py-3 bg-gray-50 border border-gray-200 rounded-full text-sm outline-none focus:ring-2 focus:ring-gray-300 placeholder:font-semibold placeholder:text-gray-400`}
                                    placeholder="DNI o RUC"
                                    disabled={isIdLocked || isLoading}
                                />

                                {isIdLocked && !isLoading && (
                                    <button
                                        type="button"
                                        onClick={handleResetCustomer}
                                        aria-label="Cambiar cliente"
                                        title="Cambiar cliente"
                                        className="h-12 w-12 shrink-0 rounded-full border border-gray-300 bg-white text-gray-700 text-xl font-semibold hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors flex items-center justify-center"
                                    >
                                        &#8635;
                                    </button>
                                )}
                            </div>

                            {!isIdLocked && !isLoading && (
                                <button
                                    onClick={handleValidateCustomer}
                                    className="w-full bg-gray-900 text-white py-3 mt-3 rounded-full font-medium hover:bg-gray-800 text-sm"
                                >
                                    Continuar
                                </button>
                            )}
                        </div>

                        {isLoading && (
                            <div className="flex justify-center items-center">
                                <ClipLoader color="#111827" size={35} />
                            </div>
                        )}

                        {error && (
                            <p className="text-red-500 text-xs text-center">{error}</p>
                        )}

                        {checkoutError && (
                            <p className="text-red-500 text-xs text-center">{checkoutError}</p>
                        )}

                        {customerData && (
                            <div className="bg-gray-50 border border-gray-200 p-3 rounded-2xl">
                                <h2 className="text-center text-sm font-bold text-gray-900">
                                    Hola, {customerData.name}
                                </h2>
                                {payments.length > 0 && (
                                    <p className="text-center text-xs text-gray-600">
                                        Selecciona la factura que quieres pagar:
                                    </p>
                                )}
                            </div>
                        )}

                        {payments.length > 0 && (
                            <PaymentPanel
                                payments={payments}
                                paymentOption={paymentOption}
                                customerType={customerData?.type}
                                selectedPayments={selectedPayments}
                                partialPaymentAmount={partialPaymentAmount}
                                appliedAmounts={appliedAmounts}
                                onPaymentOptionChange={handlePaymentOptionChange}
                                onCheckboxChange={handleCheckboxChange}
                                onPartialAmountChange={setPartialPaymentAmount}
                                onGeneralPay={handleGeneralPay}
                                isPreparingCheckout={isPreparingCheckout}
                                calculateAbonoAplicado={calculateAbonoAplicado}
                                calculatePendingBalanceOnlyForDisplay={calculatePendingBalanceOnlyForDisplay}
                                calculateExcess={calculateExcess}
                                calculateTotalPagarComplete={calculateTotalPagarComplete}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MercadoLibreInterface;
